import React, { useEffect, useState, useCallback } from "react";
import { api, CATEGORY_META } from "../lib/api";
import { useAuth } from "../lib/auth";
import TaskCard from "../components/TaskCard";
import { Search, Flame, Wallet, Trophy, Sparkles } from "lucide-react";

const CATS = ["all", ...Object.keys(CATEGORY_META)];

export default function Dashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category, search, status: "open" };
      if (user?.city) params.city = user.city;
      const { data } = await api.get("/tasks", { params });
      setTasks(data.tasks || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category, search, user?.city]);

  useEffect(() => {
    const t = setTimeout(fetchTasks, 200);
    return () => clearTimeout(t);
  }, [fetchTasks]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Greeting & stat row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 brut-card p-6 sm:p-8 bg-[var(--cs-ink)] text-white relative overflow-hidden">
          <div className="absolute -right-8 -top-8 dotgrid opacity-20 w-64 h-64 pointer-events-none" />
          <div className="text-xs uppercase tracking-widest font-bold text-slate-300">Hey {user?.name?.split(" ")[0]} 👋</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 leading-none">
            Pick up <span className="text-[var(--cs-money)]">cash</span> today.
          </h1>
          <p className="mt-3 text-slate-300 max-w-md">Browse nearby tasks in <strong className="text-white">{user?.city?.split(",")[0] || "Knoxville"}</strong>. Claim one. Earn within the hour.</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-3">
          <StatTile icon={Flame} color="var(--cs-flame)" label="Streak" value={user?.streak_days ?? 0} suffix="d" />
          <StatTile icon={Wallet} color="var(--cs-money-2)" label="Earned" value={`$${(user?.total_earned || 0).toFixed(0)}`} />
          <StatTile icon={Trophy} color="var(--cs-blue)" label="Done" value={user?.tasks_completed ?? 0} />
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--cs-ink-2)]" />
          <input
            data-testid="dashboard-search-input"
            className="brut-input pl-11"
            placeholder="Search tasks — moving, dog, groceries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => {
            const meta = c === "all" ? { label: "All", className: "" } : CATEGORY_META[c];
            const active = category === c;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c}`}
                className={`brut-chip ${active ? meta.className || "bg-[var(--cs-ink)] text-white" : "bg-white"}`}
                style={active && c === "all" ? { background: "var(--cs-ink)", color: "#fff" } : {}}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tasks grid */}
      {loading ? (
        <div className="text-center py-16 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">
          Loading tasks…
        </div>
      ) : tasks.length === 0 ? (
        <div className="brut-card p-10 text-center">
          <Sparkles className="mx-auto text-[var(--cs-flame)]" size={32} />
          <div className="font-display text-2xl font-extrabold mt-3">No tasks match yet.</div>
          <p className="text-[var(--cs-ink-2)] mt-1">Try another category or post one yourself.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tasks.map((t, i) => (
            <TaskCard key={t.id} task={t} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, color, label, value, suffix }) {
  return (
    <div className="brut-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--cs-ink-2)]">{label}</span>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="font-display tabular text-3xl font-black mt-2 leading-none" style={{ color }}>
        {value}
        {suffix && <span className="text-base ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}
